// @ai-generated 2026-08-12T13:59:56+00:00 by kai.cissarek@synqony.com using Claude Opus 5
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiContainer, type ApiContainerAction, EnumContainerOrderAction } from '@Model';
import { FrameEditContainer } from '@Modules/Stammdaten';
import { ContainerStockRepository } from '@Repositories/Auftragsbearbeitung';
import {
    type AsyncStateUpdate,
    DropdownText,
    FlexColumn,
    FlexRow,
    type ListRequest,
    LinkedField,
    Tooltip,
    useGuiBinder,
    useLinkedField,
    useLinkProperty,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo } from 'react';
import styles from './ContainerActions.module.css';
import { type StandingContainer, withStandingSites } from './standingSites';

const sortByNumber = { columnName: 'number', direction: 'asc' } as const;

interface ContainerNumberCellProps {
    action: ApiContainerAction;
    /** Path to the order; the row's own segment is appended here. */
    baseApiObjectPath: string;
    canEdit: boolean;
    /** The number the row was planned with, when another one was chosen since. */
    plannedNumber?: string;
    requestUpdateState: AsyncStateUpdate<ApiContainerAction>;
    completing: boolean;
    autoOpen?: boolean;
}

/**
 * The number of one planned container.
 *
 * Candidates come from the row's own link route rather than the master data list, because only there
 * does the server restrict them to the row's type, and for a pickup or emptying to those at the site.
 */
// @ai-generated 2026-09-28T08:36:13+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5
export function ContainerNumberCell({
    action,
    baseApiObjectPath,
    canEdit,
    plannedNumber,
    requestUpdateState,
    completing,
    autoOpen,
}: ContainerNumberCellProps) {
    const i18n = useTranslate('');
    const hasPermission = useHasPermission();

    const binder = useGuiBinder<ApiContainerAction>({ data: action, meta: undefined, canEdit, requestUpdateState });
    const linkContainer = useLinkedField<ApiContainerAction, ApiContainer>(
        `${baseApiObjectPath}/${action.id}`,
        'container',
        FrameEditContainer,
        hasPermission(Permission.StammdatenContainerCreate),
        requestUpdateState,
    );
    const containerField = useLinkProperty(binder, linkContainer.linkApi);
    const stock = useAuftragRepository(ContainerStockRepository);
    const showsStandingSites =
        action.action === EnumContainerOrderAction.Place &&
        hasPermission(Permission.AuftragsbearbeitungContainerStockRead);
    const { loadLinkableObjects } = containerField;
    const loadWithStandingSites = useCallback(
        async (request: ListRequest) =>
            withStandingSites(await loadLinkableObjects(request), (stockRequest) =>
                stock.getStockPost(stockRequest, undefined),
            ),
        [loadLinkableObjects, stock],
    );

    const columns = useMemo(() => [{ name: 'number' as const, label: i18n('Nummer') }], [i18n]);

    if (!canEdit && !action.container.foreign_key) {
        return <span className={styles.readOnlyValue}>{missingNumber(action, i18n)}</span>;
    }

    return (
        <div className={styles.numberCell}>
            <div className={styles.numberSelect}>
                <LinkedField<StandingContainer>
                    {...containerField}
                    {...linkContainer.events}
                    loadLinkableObjects={showsStandingSites ? loadWithStandingSites : loadLinkableObjects}
                    dropdownPreview={showsStandingSites ? StandingContainerPreview : undefined}
                    label={undefined}
                    placeholder={i18n('ohne Nummer')}
                    columns={columns}
                    sorting={sortByNumber}
                    required={completing}
                    autoFocusOnRender={canEdit && autoOpen}
                />
            </div>

            {plannedNumber && (
                <FlexRow
                    className={styles.plannedNumber}
                    shrink={false}
                >{`${i18n('geplant Nr.')} ${plannedNumber}`}</FlexRow>
            )}

            {completing && !action.container.foreign_key && (
                <Tooltip label={i18n('Mit Erledigt-Datum ist die Nummer Pflicht.')}>
                    <span className={styles.numberMissing}>!</span>
                </Tooltip>
            )}
        </div>
    );
}

// @ai-generated 2026-10-21T12:37:24+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5
function StandingContainerPreview({ display_string, standing_at }: StandingContainer) {
    const i18n = useTranslate('');

    return (
        <FlexColumn gap="xxs" align="stretch">
            <DropdownText content={display_string} />
            {standing_at && <FlexRow className={styles.standingAt}>{`${i18n('steht bei')} ${standing_at}`}</FlexRow>}
        </FlexColumn>
    );
}

// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function missingNumber(action: ApiContainerAction, i18n: (text: string) => string): string {
    return action.action === EnumContainerOrderAction.Place ? '—' : i18n('Nr. mit Rückmeldung');
}
